import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { ProjectAdsSidebar } from '@/components/property/ProjectAdsSidebar';
import { PropertyCard } from '@/components/property/PropertyCard';
import { FeedGrid } from '@/components/property/FeedGrid';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MapPin, Building2, Search, ChevronDown, X, Maximize, Store,
  LayoutGrid, List, Home, ChevronRight as ChevronRightIcon, ChevronLeft, ChevronRight, TrendingUp,
  Map as MapIcon
} from 'lucide-react';
import { ListingsMapView, MapItem } from '@/components/listings/ListingsMapView';
import { MapPropertyCard } from '@/components/property/MapPropertyCard';
import { formatPriceCompact } from '@/lib/formatPrice';
import { useRef, useState } from 'react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';

const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];
const COMMERCIAL_SUB_TYPES: Record<string, string> = {
  office: 'Office Space',
  retail: 'Retail / Shop',
  warehouse: 'Warehouse',
  showroom: 'Showroom',
  hotel: 'Hotel / Hospitality',
  'mixed-use': 'Mixed-Use',
};

export default function Commercial() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const initialParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [searchQuery, setSearchQuery] = useState(initialParams.get('q') || '');
  const [cityFilter, setCityFilter] = useState<string | undefined>(initialParams.get('city') || undefined);
  const [subTypeFilter, setSubTypeFilter] = useState<string | undefined>(initialParams.get('type') || initialParams.get('sub_type') || undefined);
  const [minPrice, setMinPrice] = useState<string | undefined>(initialParams.get('minPrice') || undefined);
  const [maxPrice, setMaxPrice] = useState<string | undefined>(initialParams.get('maxPrice') || undefined);
  const [minArea, setMinArea] = useState<string | undefined>(initialParams.get('minArea') || undefined);
  const [maxArea, setMaxArea] = useState<string | undefined>(initialParams.get('maxArea') || undefined);
  const [sortBy, setSortBy] = useState(initialParams.get('sort_by') || 'newest');
  const [layout, setLayout] = useState<'grid' | 'list' | 'map'>(
    initialParams.get('view') === 'map' ? 'map' : 'grid',
  );
  const trendingRef = useRef<HTMLDivElement>(null);

  const { data: properties, isLoading } = useQuery({
    queryKey: ['commercial-properties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .eq('property_type', 'commercial')
        .eq('approval_status', 'approved')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const getName = (val: any) => typeof val === 'object' ? (val?.[lang] || val?.en || '') : (val || '');
  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (price: number, currency: string) => fmtPrice(price, currency, displayCurrency, rate);
  const scroll = (dir: 'left' | 'right') => trendingRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });

  const featured = properties?.filter((p: any) => p.is_featured) || [];

  const filtered = (properties || []).filter((p: any) => {
    const title = getName(p.title).toLowerCase();
    const q = searchQuery.toLowerCase();
    if (q && !title.includes(q) && !p.city?.toLowerCase().includes(q) && !p.sub_city?.toLowerCase().includes(q)) return false;
    if (cityFilter && p.city !== cityFilter) return false;
    const features = p.features as any;
    if (subTypeFilter && features?.commercial_sub_type !== subTypeFilter) return false;
    if (minPrice && p.price < Number(minPrice)) return false;
    if (maxPrice && p.price > Number(maxPrice)) return false;
    if (minArea && (p.area_sqm || 0) < Number(minArea)) return false;
    if (maxArea && (p.area_sqm || 0) > Number(maxArea)) return false;
    return true;
  }).sort((a: any, b: any) => {
    if (sortBy === 'price_asc') return a.price - b.price;
    if (sortBy === 'price_desc') return b.price - a.price;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const activeFilterCount = [cityFilter, subTypeFilter, minPrice || maxPrice, minArea || maxArea].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0 || !!searchQuery;

  const clearAll = () => {
    setCityFilter(undefined); setSubTypeFilter(undefined);
    setMinPrice(undefined); setMaxPrice(undefined); setMinArea(undefined); setMaxArea(undefined);
    setSearchQuery('');
  };

  return (
    <Layout>
      <SEOHead
        title="Commercial Properties in Ethiopia — Office, Retail & Warehouse"
        description="Browse commercial properties for sale and rent in Ethiopia. Office spaces, retail shops, warehouses, showrooms and mixed-use buildings."
        canonical="/commercial"
      />
      <div className="border-b bg-muted/30">
        <div className="container py-4">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRightIcon className="h-3 w-3" />
            <span className="text-foreground font-medium">Commercial Properties</span>
          </nav>
          <div className="flex items-center justify-between">
            <h1 className="font-display text-xl md:text-2xl font-bold">Commercial Properties</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span> properties found
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[56px] md:top-[64px] lg:top-[117px] z-30 bg-background border-b shadow-sm">
        <div className="container">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-9 w-48 pl-8 text-xs" />
            </div>

            <Select value={cityFilter || 'all'} onValueChange={v => setCityFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <Home className="h-3.5 w-3.5 text-muted-foreground" /><SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={subTypeFilter || 'all'} onValueChange={v => setSubTypeFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <SelectValue placeholder="Sub-Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(COMMERCIAL_SUB_TYPES).map(([k, l]) => <SelectItem key={k} value={k} className="text-xs">{l}</SelectItem>)}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {minPrice || maxPrice ? `${minPrice || '0'} - ${maxPrice || '∞'} ETB` : 'Price Range'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Price Range (ETB)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Min" className="h-9 text-xs" value={minPrice || ''} onChange={e => setMinPrice(e.target.value || undefined)} />
                  <Input type="number" placeholder="Max" className="h-9 text-xs" value={maxPrice || ''} onChange={e => setMaxPrice(e.target.value || undefined)} />
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {minArea || maxArea ? `${minArea || '0'} - ${maxArea || '∞'} m²` : 'Area (m²)'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Area (m²)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Min" className="h-9 text-xs" value={minArea || ''} onChange={e => setMinArea(e.target.value || undefined)} />
                  <Input type="number" placeholder="Max" className="h-9 text-xs" value={maxArea || ''} onChange={e => setMaxArea(e.target.value || undefined)} />
                </div>
              </PopoverContent>
            </Popover>

            <div className="h-6 w-px bg-border shrink-0 mx-1" />

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>

            <div className="hidden md:flex border rounded-lg overflow-hidden shrink-0 ml-auto">
              <Button variant={layout === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('grid')} title="Grid"><LayoutGrid className="h-4 w-4" /></Button>
              <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('list')} title="List"><List className="h-4 w-4" /></Button>
              <Button variant={layout === 'map' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('map')} title="Map"><MapIcon className="h-4 w-4" /></Button>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider font-medium">Active:</span>
              {cityFilter && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{cityFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setCityFilter(undefined)} /></Badge>}
              {subTypeFilter && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{COMMERCIAL_SUB_TYPES[subTypeFilter]}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setSubTypeFilter(undefined)} /></Badge>}
              {(minPrice || maxPrice) && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{minPrice || '0'} - {maxPrice || '∞'} ETB<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { setMinPrice(undefined); setMaxPrice(undefined); }} /></Badge>}
              {(minArea || maxArea) && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{minArea || '0'} - {maxArea || '∞'} m²<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { setMinArea(undefined); setMaxArea(undefined); }} /></Badge>}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive shrink-0" onClick={clearAll}>Clear All</Button>
            </div>
          )}
        </div>
      </div>

      {/* Featured Carousel */}
      {!hasActiveFilters && featured.length > 0 && (
        <section className="container py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-bold">Featured Commercial</h2>
            <div className="flex gap-2">
              <button onClick={() => scroll('left')} className="h-9 w-9 rounded-full border bg-card flex items-center justify-center hover:bg-muted transition-colors"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => scroll('right')} className="h-9 w-9 rounded-full border bg-card flex items-center justify-center hover:bg-muted transition-colors"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div ref={trendingRef} className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {featured.map((prop: any) => {
              const title = getName(prop.title);
              const img = prop.property_images?.find((i: any) => i.is_primary)?.url || prop.property_images?.[0]?.url || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop';
              return (
                <Link key={prop.id} to={`/commercial/${prop.id}`} className="flex-shrink-0 w-[300px] md:w-[340px] rounded-xl overflow-hidden group relative">
                  <div className="relative aspect-[4/3]">
                    <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full"><TrendingUp className="h-3 w-3" /> FEATURED</span>
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg leading-tight mb-1">{title}</h3>
                      <div className="flex items-center gap-1 text-white/80 text-sm"><MapPin className="h-3.5 w-3.5" />{prop.city}{prop.sub_city ? `, ${prop.sub_city}` : ''}</div>
                    </div>
                  </div>
                  <div className="bg-card border border-t-0 rounded-b-xl p-3 space-y-1">
                    <div className="text-xs text-muted-foreground capitalize">{prop.listing_type === 'sale' ? 'For Sale' : 'For Rent'}</div>
                    <Badge variant="outline" className="text-primary border-primary font-semibold text-xs">{formatPrice(prop.price, prop.currency || 'ETB')}</Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Grid + Sidebar */}
      {layout === 'map' ? (
        <section className="container py-4">
          <ListingsMapView
            items={filtered.map((p: any): MapItem => ({
              id: p.id,
              lat: p.latitude,
              lng: p.longitude,
              title: getName(p.title) || 'Commercial',
              subtitle: `${p.city || ''}${p.sub_city ? `, ${p.sub_city}` : ''}`,
              priceLabel: formatPriceCompact(p.price, p.currency || 'ETB', displayCurrency, rate),
              image: p.property_images?.find((i: any) => i.is_primary)?.url || p.property_images?.[0]?.url,
              link: `/commercial/${p.slug || p.id}`,
            }))}
            isLoading={isLoading}
            emptyText="No commercial properties found"
            renderCard={(id, { isActive, onMouseEnter, onMouseLeave }) => {
              const p = filtered.find((x: any) => x.id === id);
              if (!p) return null;
              return (
                <div key={id} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                  <MapPropertyCard property={p} active={isActive} />
                </div>
              );
            }}
          />

        </section>
      ) : (
      <section className="container py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div>
            {isLoading ? (
              <div className={`grid gap-5 ${layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
              </div>
            ) : filtered.length > 0 ? (
              <FeedGrid items={filtered} layout={layout as 'grid' | 'list'} cardType="commercial" />

            ) : (
              <div className="text-center py-20">
                <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-lg">No commercial properties found</p>
              </div>
            )}
          </div>
          <aside className="hidden lg:block">
            <div className="sticky top-[140px] lg:top-[200px]">
              <ProjectAdsSidebar page="commercial" />
            </div>
          </aside>
        </div>
      </section>
      )}
    </Layout>
  );
}
